import { Id, IsoDate, IsoDateTime, defineAction, z } from '@mkcore/shared';
import { AUTHENTICATED } from '../permissions';

// Werk (ADR-0038; docs/04-domeinen/platform.md, Werk; CO-1385): one queue for everything that waits on a person.
// Every domain registers its work sources in platform; work.item.list merges them into work items. A read model:
// there is no table, and Werk has no write actions of its own. The main action of an item is an existing action.

/** needs_me: a decision waits on you; todo: work you do; waiting: you wait on someone else; updates: for your information. */
export const WORK_LANES = ['needs_me', 'todo', 'waiting', 'updates'] as const;
/** When a todo is due: today or late, later this week (up to Sunday), after that or without a date. */
export const WORK_BUCKETS = ['today', 'week', 'later'] as const;
/** What the person gives before the action runs: nothing, a comment, the form or call of an approval, a person, an agent. */
export const WORK_INPUTS = ['none', 'comment', 'form', 'pick-person', 'pick-agent'] as const;
/** The verb of an action; the web app shows it translated (work.actions.*). */
export const WORK_VERBS = ['approve', 'revise', 'reject', 'complete', 'assign', 'toAgent', 'markRead', 'close', 'closeDay', 'followUp', 'recordDecision'] as const;
export const WORK_PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;

export type WorkLane = (typeof WORK_LANES)[number];

export const WorkActionRef = z
  .object({
    action: z.string().describe('The action to call, by its tool name, such as approval.request.decide'),
    label: z.enum(WORK_VERBS).describe('What the action does'),
    args: z.record(z.string(), z.unknown()).describe('The input of the action; a field the person gives (input) comes on top'),
    input: z.enum(WORK_INPUTS).describe('What the person gives first; form: show the form or call of the approval, never approve in one click'),
    field: z.string().nullable().describe('The input field that takes what the person gives: comment, solution, ownerId, agentId; null for none or form'),
  })
  .openapi('WorkActionRef');

export const WorkRef = z.object({ type: z.string(), id: Id }).openapi('WorkRef');

export const WorkItem = z
  .object({
    type: z.string().describe('The kind of work: approval, day, task, issue, pull, ticket, proposal, changeRequest, notification'),
    id: Id.describe('Of the record; with type the reference of the item (?item=type:id)'),
    title: z.string(),
    key: z.string().nullable().describe('T-12, owner/repo#12, a ticket or proposal number'),
    lane: z.enum(WORK_LANES),
    bucket: z.enum(WORK_BUCKETS).nullable().describe('Todo items only: when it is due'),
    dueOn: IsoDate.nullable(),
    dueAt: IsoDateTime.nullable().describe('When the due moment has a time'),
    overdue: z.boolean(),
    priority: z.enum(WORK_PRIORITIES).nullable(),
    waitingSince: IsoDateTime.nullable().describe('Since when it waits on you (needs_me) or on someone else (waiting)'),
    reason: z.string().describe('Why it is in your queue, as a key: approve, requested, owner, assigned, review, waitingOnClient, sent, dayOpen, notification'),
    context: z.object({ type: z.string(), id: Id, label: z.string().nullable() }).nullable().describe('What it is on: a project, engagement, organisation, lead or repository'),
    target: WorkRef.nullable().describe('An approval: the object it is about; that object then gives no item of its own'),
    primary: WorkActionRef.nullable().describe('The main action; null when you may not act on it (an agent never decides)'),
    secondary: z.array(WorkActionRef),
    href: z.string().describe('The full page in the web app, such as /tasks/{id}'),
  })
  .openapi('WorkItem');

export const listWorkItems = defineAction({
  name: 'work.item.list',
  method: 'get',
  path: '/work/items',
  permission: AUTHENTICATED,
  input: z.object({
    lane: z.enum(WORK_LANES).optional().describe('One lane; left out, all of them'),
    type: z.string().optional().describe('One kind of work, such as approval or task'),
    limit: z.coerce.number().int().min(1).max(200).default(100),
  }),
  output: z.object({
    items: z.array(WorkItem),
    failed: z.array(z.string()).describe('Kinds that could not be read just now; the rest is complete'),
  }),
  summary: 'Everything that waits on you, from all domains: approvals, days to close, tasks, issues, reviews, tickets, sent proposals and change requests, notifications (CO-1385)',
  description:
    'Lanes: needs_me (decide), todo (do; bucket today, week or later), waiting (on someone else), updates (unread notifications). One thing gives one item: an open approval replaces its object. ' +
    'Each item has a primary action and secondary actions as references to existing actions. An agent that runs for a person gets the work of that person.',
});

export const countWorkItems = defineAction({
  name: 'work.item.count',
  method: 'get',
  path: '/work/items/count',
  permission: AUTHENTICATED,
  input: z.object({}),
  output: z.object({
    needsMe: z.number().int(),
    todo: z.number().int(),
    todoToday: z.number().int().describe('Todo items due today or late'),
    waiting: z.number().int(),
    updates: z.number().int(),
    badge: z.number().int().describe('The badge in the menu: needs_me plus todo due today or late'),
  }),
  summary: 'How many work items you have per lane, for the badge in the menu (CO-1385)',
});

export const workActions = [listWorkItems, countWorkItems];
